import { useEffect, useState } from "react";
import { NavLink, Navigate, Outlet, useLocation } from "react-router-dom";
import { BarChart3, MapPinned, Home, Palette, Users, Settings, LogOut, ExternalLink, Building2, Box, Wand2, HelpCircle, Brush, MessageCircle, UserRound } from "lucide-react";
import { PlanBanner } from "@/components/admin/PlanBanner";
import { ChangePasswordGate } from "@/components/admin/ChangePasswordGate";
import { terms, sellsHomes, setViewerTerms } from "@/lib/industry";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, applyTheme } from "@/lib/api";
import { useAuth, can } from "@/lib/auth";

const NAV = [
  ["", "Analytics", BarChart3, ["analytics"]], ["developments", "Developments & lots", MapPinned, ["content"]],
  ["house-types", "Homes & tours", Home, ["content", "design"]], ["editor", "3D editor", Wand2, ["content", "design"]], ["skin", "Tour skin", Brush, ["settings"]], ["assistant", "AI assistant", MessageCircle, ["settings"]], ["finishes", "Finishes & upgrades", Palette, ["design"]], ["realtors", "Realtors", UserRound, ["content"]],
  ["leads", "Leads", Users, ["leads"]], ["settings", "Branding & team", Settings, ["settings"]], ["help", "Help & guide", HelpCircle, ["analytics", "content", "design", "leads", "settings"]],
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const [builders, setBuilders] = useState([]);
  const [bid, setBid] = useState(localStorage.getItem("ih_builder") || "");
  const isSuper = user?.platform_role === "superadmin";
  const loc = useLocation();
  const onPlatform = loc.pathname.startsWith("/admin/platform");

  useEffect(() => {
    loadBuilders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const loadBuilders = () => api.get("/admin/builders").then((r) => {
    setBuilders(r.data);
    const cur = localStorage.getItem("ih_builder") || bid;
    const valid = r.data.find((b) => b.id === cur);
    const pickId = valid ? cur : r.data[0]?.id || "";
    if (isSuper && pickId) localStorage.setItem("ih_builder", pickId);
    if (!isSuper) localStorage.removeItem("ih_builder");
    setBid(isSuper ? pickId : user.builder_id);
  });
  const current = builders.find((b) => b.id === bid);
  const t = terms(current);
  setViewerTerms(current);
  const label = { developments: t.devs, "house-types": t.homes, ...(sellsHomes(current) ? {} : { leads: "Enquiries" }) };
  const nav = NAV.filter((n) => can(user, ...n[3]) && (!["finishes", "realtors"].includes(n[0]) || sellsHomes(current))).map((n) => [n[0], label[n[0]] || n[1], n[2], n[3]]);
  useEffect(() => { applyTheme(current); }, [current]);
  const change = (v) => { localStorage.setItem("ih_builder", v); setBid(v); };

  return (
    <div className="min-h-screen lg:grid lg:grid-cols-[260px_1fr] bg-[#FAF8F5]">
      <aside className="bg-bp text-white lg:min-h-screen lg:sticky lg:top-0 p-4 lg:p-6 flex lg:flex-col gap-4">
        <div className="hidden lg:block">
          <img src="/logo-light.png" alt="Showhome Interactive" className="h-10 w-auto mb-5" data-testid="admin-platform-logo" />
          <p className="text-[10px] uppercase tracking-[0.3em] text-white/50">{isSuper && onPlatform ? "Builder portal" : t.portal}</p>
          <p className="font-serif text-2xl mt-1" data-testid="admin-builder-name">{isSuper ? (onPlatform ? "Platform" : `${current?.name || "Demo"} (demo)`) : current?.name || "…"}</p>
        </div>
        {false && (
          <Select value={bid} onValueChange={change}>
            <SelectTrigger className="bg-white/10 border-white/10 text-white lg:w-full w-44" data-testid="admin-builder-switcher"><SelectValue /></SelectTrigger>
            <SelectContent>{builders.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
          </Select>
        )}
        <nav className="flex lg:flex-col gap-1 overflow-x-auto no-scrollbar flex-1">
          {isSuper && (
            <>
              <NavLink to="/admin/platform" end className={({ isActive }) => `flex items-center gap-3 px-3 h-10 rounded-xl text-sm whitespace-nowrap transition-colors border border-white/20 ${isActive ? "bg-white text-slate-900" : "hover:bg-white/10"}`} data-testid="admin-nav-platform">
                <Building2 className="w-4 h-4" /><span className="hidden sm:inline">Platform — all builders</span>
              </NavLink>
              <NavLink to="/admin/platform/3dvista" className={({ isActive }) => `flex items-center gap-3 px-3 h-10 rounded-xl text-sm whitespace-nowrap transition-colors border border-white/20 ${isActive ? "bg-white text-slate-900" : "hover:bg-white/10"}`} data-testid="admin-nav-tdv">
                <Box className="w-4 h-4" /><span className="hidden sm:inline">3DVista dashboard</span>
              </NavLink>
              <NavLink to="/admin/platform/giraffe" className={({ isActive }) => `flex items-center gap-3 px-3 h-10 rounded-xl text-sm whitespace-nowrap transition-colors border border-white/20 ${isActive ? "bg-white text-slate-900" : "hover:bg-white/10"}`} data-testid="admin-nav-g360">
                <Box className="w-4 h-4" /><span className="hidden sm:inline">Giraffe360 dashboard</span>
              </NavLink>
              <p className="hidden lg:block text-[10px] uppercase tracking-[0.25em] text-white/50 px-3 pt-4 pb-1" data-testid="admin-demo-label">Demo tours (landing page)</p>
            </>
          )}
          {nav.map(([to, label, Icon]) => (
            <NavLink key={to} to={`/admin${to ? `/${to}` : ""}`} end={!to} className={({ isActive }) => `flex items-center gap-3 px-3 h-10 rounded-xl text-sm whitespace-nowrap transition-colors ${isActive ? "bg-white text-slate-900" : "hover:bg-white/10"}`} data-testid={`admin-nav-${to || "analytics"}`}>
              <Icon className="w-4 h-4" /><span className="hidden sm:inline">{label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="hidden lg:block space-y-1 text-sm">
          {current && <a href={`/b/${current.slug}`} target="_blank" rel="noreferrer" className="flex items-center gap-2 px-3 h-9 rounded-xl hover:bg-white/10" data-testid="admin-view-site"><ExternalLink className="w-4 h-4" />View public site</a>}
          <p className="px-3 text-xs text-white/50 truncate">{user?.email}</p>
          <button onClick={logout} className="flex items-center gap-2 px-3 h-9 rounded-xl hover:bg-white/10 w-full" data-testid="admin-logout"><LogOut className="w-4 h-4" />Sign out</button>
        </div>
        <button onClick={logout} className="lg:hidden" data-testid="admin-logout-mobile"><LogOut className="w-5 h-5" /></button>
      </aside>
      <main className="p-5 sm:p-10 max-w-7xl w-full"><ChangePasswordGate />{!isSuper && bid && <PlanBanner key={loc.pathname} />}{isSuper ? (onPlatform ? <Outlet /> : !bid ? <Navigate to="/admin/platform" replace /> : <Outlet key={bid} context={{ builder: current, isSuper }} />) : loc.pathname.replace(/\/$/, "") === "/admin" && !can(user, "analytics") && nav[0] ? <Navigate to={`/admin/${nav[0][0]}`} replace /> : bid ? <Outlet key={bid} context={{ builder: current, isSuper }} /> : <p className="text-slate-500">Loading…</p>}</main>
    </div>
  );
}
